import { isSameMonth } from "date-fns";

import type { PortalUser } from "@/app/models";
import { useScheduleContext } from "@/features/consultations/contexts";
import { useAvailableDaysQuery } from "@/features/consultations/hooks";
import { LegacyCalendar } from "@/shared/ui";
import { parseBackendDate } from "@/shared/utils";

interface AvailableDaysCalendarProps {
  userId?: PortalUser["patientId"];
}

export const AvailableDaysCalendar = ({
  userId,
}: AvailableDaysCalendarProps) => {
  const {
    scheduleDate: { appointmentDate },
    actions: { setScheduleDate },
  } = useScheduleContext();

  const { data, isFetching } = useAvailableDaysQuery(userId);

  const isLoading = isFetching && !data?.length;

  const handleOffsetDateChange = (offsetDate: string) => {
    setScheduleDate((prev) => {
      const appointmentDate = prev.appointmentDate;

      const parsedOffsetDate = parseBackendDate(offsetDate);
      const parsedAppointmentDate = appointmentDate
        ? parseBackendDate(appointmentDate)
        : null;

      const isSameMonthAndYear =
        parsedAppointmentDate &&
        isSameMonth(parsedOffsetDate, parsedAppointmentDate);

      const updatedScheduleDate = {
        appointmentDate: isSameMonthAndYear ? appointmentDate : undefined,
        timeSlot: undefined,
        dateFromMonth: offsetDate,
      };

      return updatedScheduleDate;
    });
  };

  const handleOnAppointmentChange = (date: string | null) =>
    setScheduleDate((prev) => ({
      ...prev,
      appointmentDate: date ?? undefined,
      timeSlot: undefined,
    }));

  return (
    <LegacyCalendar
      value={appointmentDate ?? null}
      onChange={handleOnAppointmentChange}
      onOffsetDateChange={handleOffsetDateChange}
      availableDays={data ?? []}
      isLoading={isLoading}
    />
  );
};
